import { useDispatch, useSelector } from "react-redux";
import { selectTranslations } from "../../services/i18n/i18nSlice";
import { useNavigate } from "react-router";
import { useEffect, useState } from "react";
import { setLoading, setNavbarHide, setNavbarHideMobile } from "../../services/global/globalSlice";
import { Link, useParams } from "react-router-dom";

import { proctoringSession } from "../../utils/proctoring";
import { enterFullscreen } from "../../utils/deviceManagement";

import arrow_img from '../../assets/images/arrow-back.svg'

import { TitleH1 } from "../../components/TitleH1";
import { Text } from "../../components/Text";
import { Button } from "../../components/Button";
import { fetchGetExam, fetchStartCustomExam } from "./ExamsApi";
import { showError } from "../../features/Error/errorSlice";
import { setCurrentExam, selectCurrentProctoringSessionActive } from "../../features/Exam/examSlice";
import { redistributeTopicRows, moduleTotal, skipEmptyModules, buildTopicsMap } from "./examUtils";
import ExamCustomizeModuleCard from "./_components/ExamCustomizeModuleCard";
import ExamCustomizeTopicsSheet from "./_components/ExamCustomizeTopicsSheet";

const buildModuleTopicRows = (modules) => {
    return (modules || []).map(module => {
        if (!module.topics || module.topics.length === 0) return null
        return module.topics.map(topic => ({
            topic_ids: topic.topic_ids,
            topic_names: topic.topic_names,
            max: topic.num_questions || 1,
            // the student picks how many questions they actually want, starting from nothing
            value: 0,
            enabled: true
        }))
    })
}

const buildModuleTimes = (modules) => {
    return (modules || []).map(module => module.time)
}

const MINUTES_PER_QUESTION = 1

// exam duration isn't a fixed template value anymore: it tracks whatever the user
// actually requested, at a flat 1 minute/question, naturally split per module since
// each module's time is derived only from its own rows
const computeModuleTime = (rows) => moduleTotal(rows) * MINUTES_PER_QUESTION

// mirrors the examStarted/examCompleted computation in ExamPreviewScreen so the two
// screens agree on when a fresh, customizable attempt is actually available
const hasActiveOrCompletedAttempt = (data) => {
    const isMultipleAttempts = data?.exam?.is_multiple_attempts
    const examStarted = (data?.start_date && data?.end_date && isMultipleAttempts)
        ? false
        : !!(data?.start_date && !data?.end_date)
    const examCompleted = !!(data?.start_date && data?.end_date && !isMultipleAttempts)
    return examStarted || examCompleted
}

export default function ExamCustomizeScreen(){

    const [examData, setExamData] = useState(null)
    const [topicsMap, setTopicsMap] = useState({})
    const [moduleTopicRows, setModuleTopicRows] = useState([])
    const [moduleTimes, setModuleTimes] = useState([])
    const [moduleHasTime, setModuleHasTime] = useState([])
    const [moduleTimeCustomized, setModuleTimeCustomized] = useState([])
    const [moduleCustomized, setModuleCustomized] = useState([])
    const [moduleIncluded, setModuleIncluded] = useState([])
    const [activeSheetModuleIndex, setActiveSheetModuleIndex] = useState(null)
    const [sheetSnapshot, setSheetSnapshot] = useState(null)

    const t = useSelector(selectTranslations)
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { exam_id } = useParams()

    const proctoringSessionActive = useSelector(selectCurrentProctoringSessionActive)

    const loadExamData = async () => {
        dispatch(setLoading(true))
        // deliberately never pass a user_exam_id here: this screen only makes sense for
        // a fresh attempt (guarded below), and reusing a stale/ended one would silently
        // resume its already-elapsed progress instead of starting anew
        const response = await fetchGetExam({ exam_id: exam_id })
        if (response.success) {
            if (hasActiveOrCompletedAttempt(response.data)) {
                // an attempt is already in progress, or fully completed with no retry available:
                // structure is locked, customization no longer applies.
                // ExamPreviewScreen requires location.state.exam.is_started to trigger its own full reload,
                // otherwise it bounces to /exams for lacking state entirely.
                navigate(`/exams/preview/${exam_id}`, { replace: true, state: { exam: { exam_id, is_started: true } } })
                return
            }
            const rows = buildModuleTopicRows(response.data?.exam?.modules)
            const rawTimes = buildModuleTimes(response.data?.exam?.modules)
            const hasTime = rawTimes.map(time => time !== undefined && time !== null)
            setExamData(response.data?.exam)
            setTopicsMap(buildTopicsMap(response.data?.subjects))
            setModuleTopicRows(rows)
            // fixed (non-topic) modules keep whatever time they were fetched with: there's
            // no requested-question count to derive a duration from
            setModuleTimes(rawTimes.map((time, idx) => (hasTime[idx] && rows[idx]) ? computeModuleTime(rows[idx]) : time))
            setModuleHasTime(hasTime)
            setModuleTimeCustomized(rows.map(() => false))
            setModuleCustomized(rows.map(() => false))
            setModuleIncluded(rows.map(() => true))
            setActiveSheetModuleIndex(null)
            setSheetSnapshot(null)
            dispatch(setLoading(false))
        } else {
            console.error('fetchGetExam failed in ExamCustomizeScreen', response.error)
            dispatch(showError({
                title: t.exam.errors.start,
                text: t.exam.errors.start_text,
                redirect: "/exams",
                cta_text: t.exam.errors.go_back
            }))
        }
    }

    const handleTopicValueChange = (moduleIndex, rowIndex, rawValue) => {
        let value = parseInt(rawValue, 10)
        setModuleTopicRows(prev => prev.map((rows, mIdx) => {
            if (mIdx !== moduleIndex || !rows) return rows
            return rows.map((row, rIdx) => {
                if (rIdx !== rowIndex) return row
                const boundedValue = Number.isNaN(value) ? 0 : Math.min(Math.max(value, 0), row.max)
                return { ...row, value: boundedValue }
            })
        }))
    }

    const handleToggleTopicRow = (moduleIndex, rowIndex) => {
        setModuleTopicRows(prev => prev.map((rows, mIdx) => {
            if (mIdx !== moduleIndex || !rows) return rows
            const activeCount = rows.filter(row => row.enabled).length
            return rows.map((row, rIdx) => {
                if (rIdx !== rowIndex) return row
                // at least one topic group must stay active per module
                if (row.enabled && activeCount <= 1) return row
                return { ...row, enabled: !row.enabled }
            })
        }))
    }

    const handleToggleAllTopics = (moduleIndex) => {
        setModuleTopicRows(prev => prev.map((rows, mIdx) => {
            if (mIdx !== moduleIndex || !rows) return rows
            const allEnabled = rows.every(row => row.enabled)
            if (allEnabled) {
                // a module must keep at least one active topic group, so "deselect all" keeps the first one
                return rows.map((row, idx) => ({ ...row, enabled: idx === 0 }))
            }
            return rows.map(row => ({ ...row, enabled: true }))
        }))
    }

    const handleToggleModuleIncluded = (moduleIndex) => {
        setModuleIncluded(prev => {
            const includedCount = prev.filter(Boolean).length
            // at least one module must stay included across the whole exam
            if (prev[moduleIndex] && includedCount <= 1) return prev
            return prev.map((included, idx) => idx === moduleIndex ? !included : included)
        })
    }

    const handleTimeChange = (moduleIndex, rawValue) => {
        let value = parseInt(rawValue, 10)
        // once the user edits a module's time by hand, it stops following the
        // auto-computed default so their override isn't clobbered by later question changes
        setModuleTimeCustomized(prev => prev.map((customized, idx) => idx === moduleIndex ? true : customized))
        setModuleTimes(prev => prev.map((time, idx) => {
            if (idx !== moduleIndex) return time
            return Number.isNaN(value) ? 1 : Math.max(value, 1)
        }))
    }

    const handleStepperChange = (moduleIndex, delta) => {
        setModuleTopicRows(prev => prev.map((rows, mIdx) => {
            if (mIdx !== moduleIndex || !rows) return rows
            return redistributeTopicRows(rows, moduleTotal(rows) + delta)
        }))
    }

    const handleOpenSheet = (moduleIndex) => {
        setSheetSnapshot(moduleTopicRows[moduleIndex]?.map(row => ({ ...row })) || null)
        setActiveSheetModuleIndex(moduleIndex)
    }

    const handleSaveSheet = () => {
        setModuleCustomized(prev => prev.map((customized, idx) => idx === activeSheetModuleIndex ? true : customized))
        setActiveSheetModuleIndex(null)
        setSheetSnapshot(null)
    }

    const handleCancelSheet = () => {
        if (activeSheetModuleIndex !== null && sheetSnapshot) {
            const snapshot = sheetSnapshot
            setModuleTopicRows(prev => prev.map((rows, idx) => idx === activeSheetModuleIndex ? snapshot : rows))
        }
        setActiveSheetModuleIndex(null)
        setSheetSnapshot(null)
    }

    const handleStartCustomExam = async () => {
        dispatch(setLoading(true))
        if (examData?.proctoring_required && proctoringSessionActive) {
            if (proctoringSession?.isWindowOpen()) {
                proctoringSession.start()
            } else {
                dispatch(showError({
                    title: t.proctoring.proctoring_not_found,
                    text: t.proctoring.proctoring_not_found_text,
                    cta_text: t.exam.errors.go_back
                }))
                dispatch(setLoading(false))
                return
            }
        }
        const payload = {
            modules: (examData?.modules || []).map((_, moduleIndex) => {
                const rows = moduleTopicRows[moduleIndex]
                const modulePayload = {}
                if (rows) {
                    // an excluded module always submits an empty topic list, overriding whatever
                    // per-topic selection exists underneath (which is preserved if re-included later)
                    modulePayload.topics = moduleIncluded[moduleIndex] === false
                        ? []
                        : rows
                            .filter(row => row.enabled)
                            .map(row => ({ topic_ids: row.topic_ids, num_questions: row.value }))
                }
                // per-module time only makes sense when the exam isn't governed by a single global timer
                if (!examData?.time && moduleTimes[moduleIndex] !== undefined && moduleTimes[moduleIndex] !== null) {
                    modulePayload.time = moduleTimes[moduleIndex]
                }
                return modulePayload
            })
        }
        const response = await fetchStartCustomExam({ exam_id: exam_id }, payload)
        if (response.success) {
            dispatch(setCurrentExam({ exam_id: exam_id }))
            if (examData?.proctoring_required && proctoringSessionActive) {
                enterFullscreen()
            }
            let data = response.data
            if (!examData?.time) {
                // a student may have excluded the very first module(s): skip forward before
                // the module-by-module intro screen ever shows an empty one
                const result = await skipEmptyModules({ exam_id: exam_id, user_exam_id: data?.user_exam_id, modulesCount: data?.exam?.modules_count }, data)
                if (result.data) data = result.data
                // result.ended shouldn't be reachable given the "at least one module" guard above;
                // if it somehow is, fall through with the original data rather than blocking the student
            }
            navigate(examData?.time ? '/exams/perform/' : '/exams/start_module/', { replace: true, state: data })
        } else {
            console.error('fetchStartCustomExam failed in ExamCustomizeScreen', response.error)
            dispatch(showError({
                title: t.exam.errors.start,
                text: t.exam.errors.start_text,
                redirect: "/exams",
                cta_text: t.exam.errors.go_back
            }))
        }
    }

    useEffect(() => {
        dispatch(setNavbarHide(true))
        dispatch(setNavbarHideMobile(true))
        loadExamData()
        // eslint-disable-next-line
    }, [exam_id])

    useEffect(() => {
        setModuleTimes(prev => prev.map((time, idx) => {
            if (!moduleHasTime[idx] || moduleTimeCustomized[idx] || !moduleTopicRows[idx]) return time
            return computeModuleTime(moduleTopicRows[idx])
        }))
        // eslint-disable-next-line
    }, [moduleTopicRows])

    const includedCount = moduleIncluded.filter(Boolean).length
    const grandTotal = moduleTopicRows.reduce((sum, rows, idx) => sum + (rows && moduleIncluded[idx] !== false ? moduleTotal(rows) : 0), 0)
    const activeSheetRows = activeSheetModuleIndex !== null ? moduleTopicRows[activeSheetModuleIndex] : null

    return (
        <section id="examCustomizeScreen">
            <div className='section_header d-flex justify-content-between align-items-center'>
                <img src={arrow_img} className='pointer' alt="" onClick={() => navigate(-1)} />
                <Link to={'/account/contact'}>{t.account.need_help}</Link>
            </div>
            {examData ?
                <div style={{ maxWidth: "600px", minWidth: "320px" }} className="mx-auto exam_customize_content">
                    <TitleH1 className="text-center">{t.exam.customize.title}</TitleH1>
                    <Text className="text-center">{t.exam.customize.subtitle}</Text>

                    {examData?.modules?.map((module, moduleIndex) => (
                        <ExamCustomizeModuleCard
                            key={moduleIndex}
                            module={module}
                            rows={moduleTopicRows[moduleIndex]}
                            customized={!!moduleCustomized[moduleIndex]}
                            included={moduleIncluded[moduleIndex] !== false}
                            canToggleOff={moduleIncluded[moduleIndex] !== false ? includedCount > 1 : true}
                            showTimeInput={!examData?.time && moduleTimes[moduleIndex] !== undefined && moduleTimes[moduleIndex] !== null}
                            time={moduleTimes[moduleIndex]}
                            t={t}
                            onStepperChange={(delta) => handleStepperChange(moduleIndex, delta)}
                            onOpenSheet={() => handleOpenSheet(moduleIndex)}
                            onTimeChange={(value) => handleTimeChange(moduleIndex, value)}
                            onToggleIncluded={() => handleToggleModuleIncluded(moduleIndex)}
                        />
                    ))}

                    <ExamCustomizeTopicsSheet
                        isOpen={activeSheetModuleIndex !== null}
                        moduleName={activeSheetModuleIndex !== null ? examData?.modules?.[activeSheetModuleIndex]?.module_name : ''}
                        rows={activeSheetRows}
                        topicsMap={topicsMap}
                        t={t}
                        onToggleAll={() => handleToggleAllTopics(activeSheetModuleIndex)}
                        onToggleRow={(rowIndex) => handleToggleTopicRow(activeSheetModuleIndex, rowIndex)}
                        onValueChange={(rowIndex, value) => handleTopicValueChange(activeSheetModuleIndex, rowIndex, value)}
                        onSave={handleSaveSheet}
                        onCancel={handleCancelSheet}
                    />
                </div>
                : null}

            {examData &&
                <div className="exam_customize_footer">
                    <div className="mx-auto text-center" style={{ maxWidth: "360px" }}>
                        <Text className="mb-2">{t.exam.customize.total_label}: <strong>{grandTotal} {t.exam.customize.questions_unit}</strong></Text>
                        <Button className="mx-auto" onClick={handleStartCustomExam}>{t.exam.customize.confirm}</Button>
                        <Button color="secondary" className="mx-auto mt-2" onClick={() => navigate(-1)}>{t.exam.customize.cancel}</Button>
                    </div>
                </div>
            }
        </section>
    )
}
